import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Dataset, DatasetMappings, DatasetSubtype, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { FieldDataType, SchemaField, SystemFieldType } from '../store/data';
import { DatasetRelationHelper } from './dataset-relation.helper';
import { Document } from 'src/app/modules/core/store/models';
import { GUID_EMPTY } from '@app/core/constants';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root'
})
export class DatasetRelatedService {
  private relationHelper: DatasetRelationHelper;

  constructor(private translate: TranslateService) {
    this.relationHelper = new DatasetRelationHelper();
  }

  private sourceMappingTypesInternal: any[] = [
    { type: DatasetType.Alerts, subtype: DatasetSubtype.Intelligence },
    { type: DatasetType.Alerts, subtype: DatasetSubtype.Rating },
    { type: DatasetType.Alerts, subtype: DatasetSubtype.Weather },
    { type: DatasetType.Alerts, subtype: DatasetSubtype.TropicalCyclone },
    { type: DatasetType.Alerts, subtype: DatasetSubtype.Wildfire },
    { type: DatasetType.Alerts, subtype: DatasetSubtype.Earthquake },
    { type: DatasetType.Alerts, subtype: DatasetSubtype.MobileEvent },
    { type: DatasetType.PointOfInterest, subtype: DatasetSubtype.Area }
  ];

  private targetMappingTypesInternal: any[] = [
    { type: DatasetType.Incident, subtype: DatasetSubtype.None },
    { type: DatasetType.Assets, subtype: DatasetSubtype.Cardholder },
    { type: DatasetType.Camera, subtype: DatasetSubtype.Camera },
    { type: DatasetType.PointOfInterest, subtype: DatasetSubtype.Door }
  ];

  private defaultMappingsInternal: any[] = [
    {
      fromType: DatasetType.Alerts,
      fromSubtype: DatasetSubtype.Intelligence,
      toType: DatasetType.Incident,
      toSubtype: DatasetSubtype.None,
      mappings: [
        { fromField: 'topo-text-expression', toField: 'name', configuration: { textExpression: '{{ summary | string.slice 0 60 }}' } },
        { fromField: 'summary', toField: 'summary' },
        { fromField: 'description', toField: 'description' },
        { fromField: 'severity_number', toField: 'severity_number' },
        { fromField: 'severity_text', toField: 'severity_text' },
        { fromField: 'external_id', toField: 'external_id' },
        { fromField: 'provider', toField: 'provider' },
        { fromField: 'assignee', toField: 'assignee' },
        { fromField: 'geometry', toField: 'geometry' },
        {
          fromField: 'topo-text-expression',
          toField: 'incident_date',
          configuration: { textExpression: "{{ date.now | date.to_string '%F %T' }}" }
        }
      ]
    },
    {
      fromType: DatasetType.Alerts,
      fromSubtype: DatasetSubtype.Rating,
      toType: DatasetType.Incident,
      toSubtype: DatasetSubtype.None,
      mappings: [
        { fromField: 'name', toField: 'name' },
        { fromField: 'description', toField: 'description' },
        { fromField: 'external_id', toField: 'external_id' },
        { fromField: 'provider', toField: 'provider' },
        { fromField: 'assignee', toField: 'assignee' },
        { fromField: 'geometry', toField: 'geometry' },
        {
          fromField: 'topo-text-expression',
          toField: 'incident_date',
          configuration: { textExpression: "{{ date.now | date.to_string '%F %T' }}" }
        }
      ]
    },
    {
      fromType: DatasetType.Alerts,
      fromSubtype: DatasetSubtype.Weather,
      toType: DatasetType.Incident,
      toSubtype: DatasetSubtype.None,
      mappings: [
        { fromField: 'topo-text-expression', toField: 'name', configuration: { textExpression: '{{ summary | string.slice 0 60 }}' } },
        { fromField: 'summary', toField: 'summary' },
        { fromField: 'description', toField: 'description' },
        { fromField: 'severity_number', toField: 'severity_number' },
        { fromField: 'severity_text', toField: 'severity_text' },
        { fromField: 'external_id', toField: 'external_id' },
        { fromField: 'provider', toField: 'provider' },
        { fromField: 'assignee', toField: 'assignee' },
        { fromField: 'geometry', toField: 'geometry' },
        {
          fromField: 'topo-text-expression',
          toField: 'incident_date',
          configuration: { textExpression: "{{ date.now | date.to_string '%F %T' }}" }
        }
      ]
    },
    {
      fromType: DatasetType.Alerts,
      fromSubtype: DatasetSubtype.TropicalCyclone,
      toType: DatasetType.Incident,
      toSubtype: DatasetSubtype.None,
      mappings: [
        { fromField: 'topo-text-expression', toField: 'name', configuration: { textExpression: '{{ summary | string.slice 0 60 }}' } },
        { fromField: 'summary', toField: 'summary' },
        { fromField: 'description', toField: 'description' },
        { fromField: 'severity_number', toField: 'severity_number' },
        { fromField: 'severity_text', toField: 'severity_text' },
        { fromField: 'external_id', toField: 'external_id' },
        { fromField: 'provider', toField: 'provider' },
        { fromField: 'assignee', toField: 'assignee' },
        { fromField: 'geometry', toField: 'geometry' },
        {
          fromField: 'topo-text-expression',
          toField: 'incident_date',
          configuration: { textExpression: "{{ date.now | date.to_string '%F %T' }}" }
        }
      ]
    },
    {
      fromType: DatasetType.Alerts,
      fromSubtype: DatasetSubtype.Wildfire,
      toType: DatasetType.Incident,
      toSubtype: DatasetSubtype.None,
      mappings: [
        { fromField: 'topo-text-expression', toField: 'name', configuration: { textExpression: '{{ summary | string.slice 0 60 }}' } },
        { fromField: 'summary', toField: 'summary' },
        { fromField: 'description', toField: 'description' },
        { fromField: 'severity_number', toField: 'severity_number' },
        { fromField: 'severity_text', toField: 'severity_text' },
        { fromField: 'external_id', toField: 'external_id' },
        { fromField: 'provider', toField: 'provider' },
        { fromField: 'assignee', toField: 'assignee' },
        { fromField: 'geometry', toField: 'geometry' },
        {
          fromField: 'topo-text-expression',
          toField: 'incident_date',
          configuration: { textExpression: "{{ date.now | date.to_string '%F %T' }}" }
        }
      ]
    },
    {
      fromType: DatasetType.Alerts,
      fromSubtype: DatasetSubtype.Earthquake,
      toType: DatasetType.Incident,
      toSubtype: DatasetSubtype.None,
      mappings: [
        { fromField: 'topo-text-expression', toField: 'name', configuration: { textExpression: '{{ summary | string.slice 0 60 }}' } },
        { fromField: 'summary', toField: 'summary' },
        { fromField: 'description', toField: 'description' },
        { fromField: 'severity_number', toField: 'severity_number' },
        { fromField: 'severity_text', toField: 'severity_text' },
        { fromField: 'external_id', toField: 'external_id' },
        { fromField: 'provider', toField: 'provider' },
        { fromField: 'assignee', toField: 'assignee' },
        { fromField: 'geometry', toField: 'geometry' },
        {
          fromField: 'topo-text-expression',
          toField: 'incident_date',
          configuration: { textExpression: "{{ date.now | date.to_string '%F %T' }}" }
        }
      ]
    },
    {
      fromType: DatasetType.Alerts,
      fromSubtype: DatasetSubtype.MobileEvent,
      toType: DatasetType.Incident,
      toSubtype: DatasetSubtype.None,
      mappings: [
        { fromField: 'name', toField: 'name' },
        { fromField: 'summary', toField: 'summary' },
        { fromField: 'external_id', toField: 'external_id' },
        { fromField: 'provider', toField: 'provider' },
        { fromField: 'assignee', toField: 'assignee' },
        { fromField: 'geometry', toField: 'geometry' },
        {
          fromField: 'topo-text-expression',
          toField: 'incident_date',
          configuration: { textExpression: "{{ date.now | date.to_string '%F %T' }}" }
        }
      ]
    }
  ];

  public get sourceMappingTypes(): any[] {
    return this.sourceMappingTypesInternal;
  }

  public get targetMappingTypes(): any[] {
    return this.targetMappingTypesInternal;
  }

  public canDoSourceMapping(dataset: Dataset): boolean {
    return this.sourceMappingTypesInternal.some(m => m.type === dataset?.type && m.subtype === dataset?.subtype);
  }

  public canDoTargetMapping(dataset: Dataset): boolean {
    return this.targetMappingTypesInternal.some(m => m.type === dataset?.type && m.subtype === dataset?.subtype);
  }

  public getDefaultFieldMapping(sourceDataset: Dataset, targetDataset: Dataset): DatasetMappings {
    const mappings: DatasetMappings = {
      targetDatasetUId: targetDataset.uId,
      targetMappings: []
    };

    const defaultMapping = this.defaultMappingsInternal.filter(
      m =>
        m.fromType === sourceDataset?.type &&
        m.fromSubtype === sourceDataset?.subtype &&
        m.toType === targetDataset?.type &&
        m.toSubtype === targetDataset?.subtype
    );

    if (!!defaultMapping && defaultMapping.length) {
      defaultMapping[0].mappings.map(m => mappings.targetMappings.push(m));
    }

    return mappings;
  }

  public getRelatedParentFieldByDataset(dataset: Dataset, toDataset: Dataset): SchemaField {
    return this.relationHelper.getRelatedParentFieldByDataset(dataset, toDataset);
  }

  public getRelatedParentFieldByDocument(dataset: Dataset, document: Document): SchemaField {
    return this.relationHelper.getRelatedParentFieldByDocument(dataset, document);
  }

  public getRelatedDatasetTypeByDataset(fromDataset: Dataset, toDataset: Dataset): { type: DatasetType; subtype: DatasetSubtype } {
    return this.relationHelper.getRelatedDatasetTypeByDataset(fromDataset, toDataset);
  }

  public getRelatedDatasetTypeByDocument(fromDataset: Dataset, document: Document): { type: DatasetType; subtype: DatasetSubtype } {
    return this.relationHelper.getRelatedDatasetTypeByDocument(fromDataset, document);
  }

  public getRelatedParentFieldNameByDataset(fromDataset: Dataset, toDataset: Dataset): string {
    const relType = this.relationHelper.getRelatedDatasetTypeByDataset(fromDataset, toDataset);
    if (!relType?.type) {
      return null;
    }

    const typeName = `${this.translate.instant('MANAGEMENT.DATASETS.DATASET-TYPE-' + relType.type)}`;
    const subtypeName = `${this.translate.instant('MANAGEMENT.DATASETS.DATASET-SUBTYPE-' + relType.subtype)}`;

    return `${typeName.toLowerCase()}_${subtypeName.toLowerCase()}_items`;
  }

  public getRelatedParentFieldNameByDocument(fromDataset: Dataset, document: Document): string {
    const relType = this.relationHelper.getRelatedDatasetTypeByDocument(fromDataset, document);
    if (!relType?.type) {
      return null;
    }

    const typeName = `${this.translate.instant('MANAGEMENT.DATASETS.DATASET-TYPE-' + relType.type)}`;
    const subtypeName = `${this.translate.instant('MANAGEMENT.DATASETS.DATASET-SUBTYPE-' + relType.subtype)}`;

    return `${typeName.toLowerCase()}_${subtypeName.toLowerCase()}_items`;
  }

  public createRelatedParentField(fromDataset: Dataset, toDataset: Dataset): SchemaField {
    const relType = this.relationHelper.getRelatedDatasetTypeByDataset(fromDataset, toDataset);
    if (!relType?.type) {
      return null;
    }

    const systemName = this.getRelatedParentFieldNameByDataset(fromDataset, toDataset);
    const typeName = `${this.translate.instant('MANAGEMENT.DATASETS.DATASET-TYPE-' + relType.type)}`;
    const subtypeName = `${this.translate.instant('MANAGEMENT.DATASETS.DATASET-SUBTYPE-' + relType.subtype)}`;

    return {
      uId: uuidv4(),
      dataType: FieldDataType.RelatedParent,
      fieldType: SystemFieldType.System | SystemFieldType.Editable,
      systemName: systemName,
      displayName: `${typeName} ${subtypeName} items`,
      settings: {
        relatedDatasetType: relType.type,
        relatedDatasetSubType: relType.subtype,
        relatedDatasetUId: GUID_EMPTY
      }
    } as SchemaField;
  }
}
